"use client";

/**
 * The per-package sentences every buying surface renders instead of the old site-wide badges
 * ("30 Gün Garanti", "Anında Başlar"). BUG-CAT-04, ADR-005 §1, ADR-006 Q3, Claim-Matrix §4.3.
 *
 * - Refill (G03, owner decisions D-06/D-07/D-08): every package has a refill that SociShop funds.
 *   Its length comes from the package's USD list price through lib/refill-policy.ts — 30, 90 or
 *   365 days — never from the supplier's wording and never from a site-wide number. A package whose
 *   USD list price is unknown gets the rule ("set by the USD list price"), not a guessed number.
 * - Start: fulfilment is manual. Show a catalogue window only when it is at
 *   least 24 hours; omit the generic start claim when no honest window exists.
 *   A leftover "0-2 saat" (stale snapshot, cached API answer) never reaches the page.
 *
 * The keys are called one by one (no computed key) so lib/__tests__/i18n-parity
 * can prove each one is defined in all six locales and reachable from code.
 */
import { useCallback } from "react";
import { useTranslations } from "next-intl";
import { honestStartWindow } from "@/lib/claims/package-claims";
import { localizeDeliveryTime } from "@/lib/localize-delivery";
import type { RefillDays } from "@/lib/refill-policy";

/**
 * `(days) => sentence` for a package's refill: "90-day refill" from the policy days
 * (lib/refill-policy.ts `refillDaysForPackage`), or the rule itself when the days are unknown.
 * `variant: "summary"` is the longer sentence for the selected-package box and the checkout.
 */
export function useRefillLine(): (days: RefillDays | null, variant?: "card" | "summary") => string {
    const tc = useTranslations("claimTerms");
    return useCallback((days: RefillDays | null, variant: "card" | "summary" = "card") => {
        if (days === null) return tc("refill_basis");
        return variant === "summary"
            ? tc("refill_days.summary", { days })
            : tc("refill_days.card", { days });
    }, [tc]);
}

/** `(catalogueWindow) => sentence` for a verified start window; empty otherwise. */
export function useStartLine(locale: string): (catalogueWindow: string | null | undefined) => string {
    const tc = useTranslations("claimTerms");
    return useCallback((catalogueWindow: string | null | undefined) => {
        const window = honestStartWindow(catalogueWindow);
        return window
            ? tc("startWindow", { window: localizeDeliveryTime(window, locale) })
            : "";
    }, [tc, locale]);
}

/*
 * G04 (D-12, 2026-10-02): `useSupplierDisclosureLine` ("Supplier states: bot accounts …") and `useCompletionLine`
 * ("about N days at the supplier's stated pace") were removed with their components. Supplier panel names,
 * categories and labels are never a source of customer-facing text; the quality statement is the owner
 * attestation (lib/claims/owner-attestation.ts).
 */
